/**
 * Preflight WebGL before mounting Three.js / R3F canvases.
 *
 * Three's WebGLCapabilities does `gl.getShaderPrecisionFormat(...).precision`
 * without a null check. When the context is missing, lost, or soft-fails
 * (common under context exhaustion from many portal Orbs), that throws and
 * takes down the product surface. Error boundaries do **not** catch throws
 * from R3F Canvas `useLayoutEffect`, so callers must gate mount.
 */
export function isWebGLUsable(): boolean {
	if (typeof document === "undefined") {
		return false;
	}
	try {
		const canvas = document.createElement("canvas");
		const gl =
			canvas.getContext("webgl", {
				failIfMajorPerformanceCaveat: false,
			}) ??
			canvas.getContext("experimental-webgl", {
				failIfMajorPerformanceCaveat: false,
			});
		if (!gl || typeof (gl as WebGLRenderingContext).getShaderPrecisionFormat !== "function") {
			return false;
		}
		const webgl = gl as WebGLRenderingContext;
		const precision = webgl.getShaderPrecisionFormat(
			webgl.VERTEX_SHADER,
			webgl.HIGH_FLOAT,
		);
		if (!precision || typeof precision.precision !== "number") {
			return false;
		}
		webgl.getExtension("WEBGL_lose_context")?.loseContext();
		return true;
	} catch {
		return false;
	}
}
